import React, { useCallback, useEffect, useRef, useState } from "react";
import { authService, clearSession, saveSession } from "./api/auth";
import { SESSION_INVALID_EVENT } from "./api/client";
import { chatService } from "./api/chats";
import { organizerService } from "./api/organizer";
import { connectSocket, socketEventChatToRefresh } from "./api/socket";
import { isTransientHttpError, retryRequest } from "./api/retry";
import { pushService, PushPromptMode } from "./api/push";
import ChatList from "./components/ChatList";
import ChatWindow from "./components/ChatWindow";
import Login from "./components/Login";
import { NotificationPrompt } from "./components/NotificationPrompt";
import { TeacherAssistantPanel } from "./components/TeacherAssistantPanel";
import { TeacherAssistantMode } from "./api/teacherAssistant";
import { teacherAssistantService } from "./api/teacherAssistant";
import {
  armNotificationSound,
  playNotificationSound,
  shouldPlayNotificationSound,
} from "./utils/notificationSound";
import {
  isCurrentChatRequest,
  shouldReuseChatRequest,
} from "./utils/chatRequest";
import {
  Chat,
  ChatGroup,
  Message,
  OnlineStaffUser,
  QuickReply,
  QuickReplyDirection,
} from "./types";
import "./App.css";

// Safari (especially in private mode and embedded/mobile contexts) may report
// hasFocus() as false during the first visible render. Visibility is reliable
// for that initial state; explicit focus/blur events refine it afterwards.
const isPageInitiallyActive = () => document.visibilityState === "visible";

const App = () => {
  const [authenticated, setAuthenticated] = useState(
    false,
  );
  const [checking, setChecking] = useState(true);
  const [accessError, setAccessError] = useState("");
  const [chats, setChats] = useState<Chat[]>([]);
  const [chatTotal, setChatTotal] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedDetails, setSelectedDetails] = useState<Chat | null>(null);
  const chatCache = useRef(new Map<string, Chat>());
  const chatQuery = useRef("");
  const chatListGeneration = useRef(0);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loadingChats, setLoadingChats] = useState(false);
  const [loadingMoreChats, setLoadingMoreChats] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [appError, setAppError] = useState("");
  const [syncDelayed, setSyncDelayed] = useState(false);
  const [webhookHealthy, setWebhookHealthy] = useState(true);
  const [onlineUsers, setOnlineUsers] = useState<OnlineStaffUser[]>([]);
  const [pageActive, setPageActive] = useState(isPageInitiallyActive);
  const [pushPrompt, setPushPrompt] = useState<PushPromptMode>(null);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState("");
  const [draftsByChat, setDraftsByChat] = useState<Record<string, string>>(
    () => {
      try {
        const value = JSON.parse(
          localStorage.getItem("avitoChatDrafts") || "{}",
        );
        return value && typeof value === "object" ? value : {};
      } catch {
        return {};
      }
    },
  );
  const [groups, setGroups] = useState<ChatGroup[]>([]);
  const [replies, setReplies] = useState<QuickReply[]>([]);
  const [replyDirections, setReplyDirections] = useState<QuickReplyDirection[]>(
    [],
  );
  const [insertRequest, setInsertRequest] = useState<{
    id: number;
    text: string;
  } | null>(null);
  const [quickRepliesRequest, setQuickRepliesRequest] = useState(0);
  const [teacherAssistant, setTeacherAssistant] = useState<{
    mode: TeacherAssistantMode;
    chatId?: string;
    chatName?: string;
  } | null>(null);
  const [teacherAssistantEnabled, setTeacherAssistantEnabled] = useState(false);
  const [teacherAssistantCurriculaReady, setTeacherAssistantCurriculaReady] = useState(false);
  const chatListRequest = useRef<Promise<void> | null>(null);
  const messageRequests = useRef(new Map<string, Promise<void>>());
  const selectedIdRef = useRef<string | null>(null);
  const updateSelectedId = useCallback((chatId: string | null) => {
    selectedIdRef.current = chatId;
    setSelectedId(chatId);
    if (!chatId) setSelectedDetails(null);
  }, []);

  const loadChats = useCallback(() => {
    if (chatListRequest.current) return chatListRequest.current;
    const query = chatQuery.current;
    const generation = ++chatListGeneration.current;
    const request = (async () => {
      setLoadingChats(true);
      setLoadingMoreChats(false);
      try {
        const page = await retryRequest(() => query
          ? chatService.list(0, 50, query)
          : chatService.list(0, 50));
        if (generation !== chatListGeneration.current) return;
        page.items.forEach((chat) => chatCache.current.set(chat.id, chat));
        setChats(page.items);
        setChatTotal(page.total);
        setAppError("");
      } catch (error: any) {
        if (generation !== chatListGeneration.current) return;
        setAppError(
          error.response?.data?.detail || "Не удалось загрузить чаты Avito",
        );
      } finally {
        if (generation === chatListGeneration.current) setLoadingChats(false);
      }
    })();
    chatListRequest.current = request;
    void request.finally(() => {
      if (chatListRequest.current === request) chatListRequest.current = null;
    });
    return request;
  }, []);
  const searchChats = useCallback((value: string) => {
    const query = value.trim();
    if (chatQuery.current === query) return;
    chatQuery.current = query;
    chatListRequest.current = null;
    setChats([]);
    setChatTotal(0);
    setLoadingMoreChats(false);
    void loadChats();
  }, [loadChats]);
  const loadMoreChats = useCallback(async () => {
    if (loadingMoreChats || chats.length >= chatTotal) return;
    const query = chatQuery.current;
    const generation = chatListGeneration.current;
    setLoadingMoreChats(true);
    try {
      const page = await retryRequest(() =>
        query ? chatService.list(chats.length, 50, query) : chatService.list(chats.length, 50),
      );
      if (generation !== chatListGeneration.current) return;
      page.items.forEach((chat) => chatCache.current.set(chat.id, chat));
      setChats((current) => {
        const merged = new Map(current.map((chat) => [chat.id, chat]));
        page.items.forEach((chat) => merged.set(chat.id, chat));
        return Array.from(merged.values());
      });
      setChatTotal(page.total);
    } catch (error: any) {
      if (generation !== chatListGeneration.current) return;
      setAppError(
        error.response?.data?.detail || "Не удалось загрузить следующую страницу",
      );
    } finally {
      if (generation === chatListGeneration.current) setLoadingMoreChats(false);
    }
  }, [chatTotal, chats.length, loadingMoreChats]);
  const loadMessages = useCallback((chatId: string, replace = false) => {
    const active = messageRequests.current.get(chatId);
    if (shouldReuseChatRequest(Boolean(active), replace) && active)
      return active;
    const request = (async () => {
      if (isCurrentChatRequest(selectedIdRef.current, chatId))
        setLoadingMessages(true);
      try {
        const page = await retryRequest(() => chatService.messages(chatId));
        if (!isCurrentChatRequest(selectedIdRef.current, chatId)) return;
        setMessages((current) => {
          if (replace) return page;
          const merged = new Map(
            current.map((message) => [message.id, message]),
          );
          page.forEach((message) => merged.set(message.id, message));
          return Array.from(merged.values()).sort(
            (a, b) =>
              new Date(a.created_at).getTime() -
              new Date(b.created_at).getTime(),
          );
        });
        if (!isCurrentChatRequest(selectedIdRef.current, chatId)) return;
        try {
          await retryRequest(() => chatService.read(chatId));
          if (isCurrentChatRequest(selectedIdRef.current, chatId))
            setChats((items) =>
              items.map((item) =>
                item.id === chatId ? { ...item, unreadCount: 0 } : item,
              ),
            );
        } catch {
          // History is loaded; a later refresh can retry the read state.
        }
        if (isCurrentChatRequest(selectedIdRef.current, chatId)) setAppError("");
      } catch (error: any) {
        if (isCurrentChatRequest(selectedIdRef.current, chatId))
          setAppError(
            error.response?.data?.detail ||
              "Не удалось загрузить сообщения Avito",
          );
      } finally {
        if (isCurrentChatRequest(selectedIdRef.current, chatId))
          setLoadingMessages(false);
      }
    })();
    messageRequests.current.set(chatId, request);
    void request.finally(() => {
      if (messageRequests.current.get(chatId) === request)
        messageRequests.current.delete(chatId);
    });
    return request;
  }, []);
  const loadOrganizer = useCallback(async () => {
    try {
      const [g, d, r] = await Promise.all([
        organizerService.groups(),
        organizerService.replyDirections(),
        organizerService.replies(),
      ]);
      setGroups(g);
      setReplyDirections(d);
      setReplies(r);
    } catch (error: any) {
      setAppError(
        error.response?.data?.detail || "Не удалось загрузить группы и ответы",
      );
    }
  }, []);

  useEffect(() => {
    let active = true;
    const check = async () => {
      const url = new URL(window.location.href);
      const sso = url.searchParams.get("sso_token");
      const fragment = new URLSearchParams(url.hash.replace(/^#/, ""));
      const launchCode = fragment.get("launch_code");
      try {
        if (launchCode) {
          const response = await authService.exchangeLaunchCode(launchCode);
          saveSession(response);
          fragment.delete("launch_code");
          fragment.delete("target_service");
          url.hash = fragment.toString() ? `#${fragment.toString()}` : "";
          window.history.replaceState(
            {},
            "",
            `${url.pathname}${url.search}${url.hash}`,
          );
        } else if (sso) {
          const response = await authService.exchangeSsoToken(sso);
          saveSession(response);
          url.searchParams.delete("sso_token");
          window.history.replaceState(
            {},
            "",
            `${url.pathname}${url.search}${url.hash}`,
          );
        } else await authService.getMe();
        if (active) setAuthenticated(true);
      } catch (error: any) {
        clearSession();
        if (active) {
          setAuthenticated(false);
          setAccessError(
            error.response?.data?.detail || "Не удалось авторизоваться",
          );
        }
      } finally {
        if (active) setChecking(false);
      }
    };
    void check();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (authenticated) {
      void loadChats();
      void loadOrganizer();
    } else {
      setTeacherAssistantEnabled(false);
      setTeacherAssistantCurriculaReady(false);
    }
  }, [authenticated, loadChats, loadOrganizer]);
  useEffect(() => {
    if (!authenticated) {
      setSyncDelayed(false);
      setWebhookHealthy(true);
      return;
    }
    let active = true;
    const refresh = async () => {
      try {
        const status = await chatService.syncStatus();
        if (!active) return;
        setSyncDelayed(Boolean(status.delayed));
        setWebhookHealthy(Boolean(status.webhook.healthy));
      } catch {
        // Chat/API errors are already surfaced by their own requests. Do not
        // replace them with a diagnostic-status polling error.
      }
    };
    void refresh();
    const timer = window.setInterval(() => void refresh(), 30000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [authenticated]);
  useEffect(() => {
    if (!authenticated) return;
    let active = true;
    let requesting = false;
    let retryTimer: number | null = null;
    let retryDelay = 2000;

    const refreshStatus = async () => {
      if (!active || requesting) return;
      if (retryTimer !== null) {
        window.clearTimeout(retryTimer);
        retryTimer = null;
      }
      requesting = true;
      try {
        const result = await teacherAssistantService.status();
        if (active) {
          setTeacherAssistantEnabled(Boolean(result?.enabled));
          setTeacherAssistantCurriculaReady(Boolean(result?.curricula_ready));
          retryDelay = 2000;
          if (result?.enabled && !result?.curricula_ready) {
            retryTimer = window.setTimeout(() => {
              retryTimer = null;
              void refreshStatus();
            }, 5000);
          }
        }
      } catch (error: any) {
        if (!active || error?.response?.status === 401) return;
        retryTimer = window.setTimeout(() => {
          retryTimer = null;
          void refreshStatus();
        }, retryDelay);
        retryDelay = Math.min(retryDelay * 2, 30000);
      } finally {
        requesting = false;
      }
    };
    const refreshWhenActive = () => {
      if (document.visibilityState === "visible") void refreshStatus();
    };

    void refreshStatus();
    window.addEventListener("focus", refreshWhenActive);
    document.addEventListener("visibilitychange", refreshWhenActive);
    return () => {
      active = false;
      if (retryTimer !== null) window.clearTimeout(retryTimer);
      window.removeEventListener("focus", refreshWhenActive);
      document.removeEventListener("visibilitychange", refreshWhenActive);
    };
  }, [authenticated]);
  useEffect(() => {
    if (!authenticated || checking) return;
    const mode = pushService.promptMode();
    if (mode && !sessionStorage.getItem("avitoPushPromptDismissed"))
      setPushPrompt(mode);
    if (!mode)
      void pushService.ensureSubscribed().catch((error) => {
        setPushError(
          error instanceof Error
            ? error.message
            : "Не удалось восстановить подписку на уведомления",
        );
        setPushPrompt("repair");
      });
  }, [authenticated, checking]);
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const openNotificationChat = (event: MessageEvent) => {
      if (event.data?.type !== "OPEN_CHAT") return;
      const chatId = String(event.data?.chat_id || "");
      if (!chatId) return;
      const url = new URL(window.location.href);
      url.searchParams.set("chat", chatId);
      window.history.replaceState(
        {},
        "",
        `${url.pathname}${url.search}${url.hash}`,
      );
      setLoadingMessages(true);
      updateSelectedId(chatId);
    };
    navigator.serviceWorker.addEventListener("message", openNotificationChat);
    return () =>
      navigator.serviceWorker.removeEventListener(
        "message",
        openNotificationChat,
      );
  }, [updateSelectedId]);
  useEffect(() => {
    const requestedChatId = new URLSearchParams(window.location.search).get(
      "chat",
    );
    if (
      !requestedChatId ||
      !authenticated ||
      selectedId === requestedChatId
    )
      return;
    setLoadingMessages(true);
    updateSelectedId(requestedChatId);
  }, [authenticated, selectedId, updateSelectedId]);
  useEffect(() => {
    if (!authenticated || !selectedId) return;
    const cached = chatCache.current.get(selectedId);
    if (cached) {
      setSelectedDetails(cached);
      return;
    }
    let active = true;
    void retryRequest(() => chatService.get(selectedId)).then((chat) => {
      if (!active) return;
      chatCache.current.set(chat.id, chat);
      setSelectedDetails(chat);
    }).catch((error) => {
      if (active) setAppError(error.response?.data?.detail || "Не удалось открыть чат Avito");
    });
    return () => { active = false; };
  }, [authenticated, selectedId]);
  useEffect(() => {
    if (Object.keys(draftsByChat).length)
      localStorage.setItem("avitoChatDrafts", JSON.stringify(draftsByChat));
    else localStorage.removeItem("avitoChatDrafts");
  }, [draftsByChat]);
  useEffect(() => {
    if (!appError) return;
    const timeout = window.setTimeout(() => setAppError(""), 6000);
    return () => window.clearTimeout(timeout);
  }, [appError]);
  useEffect(() => {
    const setPresenceFromVisibility = () => {
      const active = document.visibilityState === "visible";
      setPageActive(active);
      if (!active) setOnlineUsers([]);
    };
    const activatePresence = () => {
      if (document.visibilityState === "visible") setPageActive(true);
    };
    const deactivatePresence = () => {
      setPageActive(false);
      setOnlineUsers([]);
    };

    document.addEventListener("visibilitychange", setPresenceFromVisibility);
    window.addEventListener("focus", activatePresence);
    window.addEventListener("blur", deactivatePresence);
    return () => {
      document.removeEventListener("visibilitychange", setPresenceFromVisibility);
      window.removeEventListener("focus", activatePresence);
      window.removeEventListener("blur", deactivatePresence);
    };
  }, []);

  useEffect(() => {
    const handleSessionInvalid = () => {
      clearSession();
      setAuthenticated(false);
      setChats([]);
      chatCache.current.clear();
      chatQuery.current = "";
      chatListGeneration.current += 1;
      chatListRequest.current = null;
      setMessages([]);
      updateSelectedId(null);
      setTeacherAssistant(null);
      setAccessError("Сессия завершена. Войдите снова.");
    };
    window.addEventListener(SESSION_INVALID_EVENT, handleSessionInvalid);
    return () => window.removeEventListener(SESSION_INVALID_EVENT, handleSessionInvalid);
  }, [updateSelectedId]);

  useEffect(() => {
    if (!authenticated) return;
    if (!pageActive) return;
    const disarmSound = armNotificationSound();
    const disconnect = connectSocket((event) => {
      const activeChatId = selectedIdRef.current;
      if (event.type === "presence_updated")
        setOnlineUsers(Array.isArray(event.users) ? event.users : []);
      if (
        shouldPlayNotificationSound(
          event,
          activeChatId,
          document.visibilityState === "visible",
        )
      )
        playNotificationSound();
      if (
        event.type === "quick_replies_updated" ||
        event.type === "groups_updated"
      )
        void loadOrganizer();
      if (
        event.type === "chat_groups_updated" ||
        event.type === "groups_updated" ||
        event.type === "chat_read" ||
        event.type === "chat_updated" ||
        event.type === "chats_synced"
      )
        void loadChats();
      const chatToRefresh = socketEventChatToRefresh(event, activeChatId);
      if (chatToRefresh) void loadMessages(chatToRefresh);
    });
    return () => {
      disarmSound();
      disconnect();
      setOnlineUsers([]);
    };
  }, [
    authenticated,
    loadChats,
    loadMessages,
    loadOrganizer,
    pageActive,
  ]);
  useEffect(() => {
    setMessages([]);
    if (selectedId) void loadMessages(selectedId, true);
  }, [selectedId, loadMessages]);

  const appendMessage = (message: Message) =>
    setMessages((items) =>
      items.some((item) => item.id === message.id)
        ? items
        : [...items, message],
    );
  const sendText = async (text: string) => {
    if (!selectedId) return;
    setSending(true);
    try {
      appendMessage(await chatService.sendText(selectedId, text));
      void loadChats();
    } finally {
      setSending(false);
    }
  };
  const sendImage = async (file: File) => {
    if (!selectedId) return;
    const idempotencyKey = window.crypto.randomUUID();
    setSending(true);
    try {
      appendMessage(
        await retryRequest(
          () => chatService.sendImage(selectedId, file, idempotencyKey),
          3,
          500,
          isTransientHttpError,
        ),
      );
      void loadChats();
    } finally {
      setSending(false);
    }
  };
  const deleteMessage = async (messageId: string) => {
    if (!selectedId) return;
    const deleted = await chatService.deleteMessage(selectedId, messageId);
    setMessages((items) =>
      items.map((message) => (message.id === deleted.id ? deleted : message)),
    );
    void loadChats();
  };
  const changeDraft = (chatId: string, value: string) =>
    setDraftsByChat((current) => {
      if (!value.trim()) {
        const next = { ...current };
        delete next[chatId];
        return next;
      }
      return { ...current, [chatId]: value };
    });
  const assignGroups = async (chatId: string, ids: string[]) => {
    const previous = chats.find((chat) => chat.id === chatId)?.groups || [];
    const next = groups.filter((group) => ids.includes(group.id));
    setChats((items) =>
      items.map((chat) =>
        chat.id === chatId ? { ...chat, groups: next } : chat,
      ),
    );
    try {
      await organizerService.assignGroups(chatId, ids);
    } catch (error) {
      setChats((items) =>
        items.map((chat) =>
          chat.id === chatId ? { ...chat, groups: previous } : chat,
        ),
      );
      throw error;
    }
  };
  const updateGroup = async (
    id: string,
    payload: Partial<Pick<ChatGroup, "name" | "color">>,
  ) => {
    const previous = groups.find((group) => group.id === id);
    if (!previous) return;
    const next = { ...previous, ...payload };
    const apply = (group: ChatGroup) =>
      group.id === id ? { ...group, ...payload } : group;
    setGroups((items) =>
      items.map((group) => (group.id === id ? next : group)),
    );
    setChats((items) =>
      items.map((chat) => ({
        ...chat,
        groups: (chat.groups || []).map(apply),
      })),
    );
    try {
      await organizerService.updateGroup(id, payload);
    } catch (error) {
      setGroups((items) =>
        items.map((group) => (group.id === id ? previous : group)),
      );
      setChats((items) =>
        items.map((chat) => ({
          ...chat,
          groups: (chat.groups || []).map((group) =>
            group.id === id ? previous : group,
          ),
        })),
      );
      throw error;
    }
  };
  const createGroup = async (name: string, color: string) => {
    const temporaryId = `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const optimistic: ChatGroup = {
      id: temporaryId,
      name,
      color,
      sort_order: groups.length,
    };
    setGroups((items) => [...items, optimistic]);
    try {
      const created = await organizerService.createGroup(name, color);
      setGroups((items) => {
        const next = items.filter(
          (group) => group.id !== temporaryId && group.id !== created.id,
        );
        return [...next, created].sort(
          (left, right) =>
            (left.sort_order ?? Number.MAX_SAFE_INTEGER) -
            (right.sort_order ?? Number.MAX_SAFE_INTEGER),
        );
      });
    } catch (error: any) {
      setGroups((items) => items.filter((group) => group.id !== temporaryId));
      setAppError(
        error.response?.data?.detail || "Не удалось создать группу клиентов",
      );
      throw error;
    }
  };
  const logout = async () => {
    try {
      await authService.logout();
      clearSession();
      setAuthenticated(false);
      chatCache.current.clear();
      setChats([]);
      chatQuery.current = "";
      chatListGeneration.current += 1;
      chatListRequest.current = null;
      updateSelectedId(null);
      setTeacherAssistant(null);
    } catch {
      setAppError("Не удалось выйти. Проверьте соединение и повторите попытку.");
    }
  };
  const allowPush = async () => {
    setPushBusy(true);
    setPushError("");
    try {
      await pushService.subscribe();
      setPushPrompt(null);
    } catch (error) {
      setPushError(error instanceof Error ? error.message : "Не удалось подключить уведомления");
    } finally {
      setPushBusy(false);
    }
  };
  const closePushPrompt = () => {
    sessionStorage.setItem("avitoPushPromptDismissed", "1");
    setPushPrompt(null);
  };
  const selectChat = (id: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set("chat", id);
    window.history.replaceState(
      {},
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
    setLoadingMessages(true);
    updateSelectedId(id);
  };
  const closeChat = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("chat");
    window.history.replaceState(
      {},
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
    updateSelectedId(null);
  };

  if (!authenticated && !checking)
    return (
      <div className="App">
        {accessError && <div className="avito-access-error">{accessError}</div>}
        <Login
          onLogin={() => {
            setAccessError("");
            setAuthenticated(true);
          }}
        />
      </div>
    );
  const selected = chats.find((chat) => chat.id === selectedId)
    || (selectedDetails?.id === selectedId ? selectedDetails : undefined);
  return (
    <div className="App">
      {pushPrompt && (
        <NotificationPrompt
          mode={pushPrompt}
          busy={pushBusy}
          error={pushError}
          onAllow={() => void allowPush()}
          onClose={closePushPrompt}
        />
      )}
      {teacherAssistant && (
        <TeacherAssistantPanel
          key={`${teacherAssistant.mode}-${teacherAssistant.chatId || "standalone"}`}
          mode={teacherAssistant.mode}
          chatId={teacherAssistant.chatId}
          chatName={teacherAssistant.chatName}
          curriculaReady={teacherAssistantCurriculaReady}
          onClose={() => setTeacherAssistant(null)}
        />
      )}
      <div className={`chat-app ${selectedId ? "show-chat-window" : ""}`}>
        {syncDelayed && (
          <div className="sync-delay-notice" role="status">
            Синхронизация с Avito временно задержана. Уже загруженные чаты доступны
            {webhookHealthy
              ? ", новые сообщения продолжают поступать через webhook."
              : ". Приём новых сообщений также восстанавливается автоматически."}
          </div>
        )}
        {appError && (
          <div className="site-notice visible">
            <div className="site-notice-content">
              <span>{appError}</span>
              <button
                className="site-notice-close"
                onClick={() => setAppError("")}
              >
                ×
              </button>
            </div>
          </div>
        )}
        <div className="chat-list-container">
          <ChatList
            chats={chats}
            onlineUsers={onlineUsers}
            groups={groups}
            replies={replies}
            replyDirections={replyDirections}
            draftsByChat={draftsByChat}
            selectedChatId={selectedId}
            loading={checking || loadingChats}
            loadingMore={loadingMoreChats}
            hasMore={chats.length < chatTotal}
            onLoadMore={loadMoreChats}
            onSearchChange={searchChats}
            onSelect={selectChat}
            onLogout={logout}
            onOpenTeacherAssistant={teacherAssistantEnabled
              ? () => setTeacherAssistant({ mode: "standalone" })
              : undefined}
            onAssign={assignGroups}
            onCreateGroup={createGroup}
            onUpdateGroup={updateGroup}
            onDeleteGroup={async (id) => {
              await organizerService.deleteGroup(id);
              await loadOrganizer();
              await loadChats();
            }}
            onCreateTopic={async (directionId, name) => {
              const topic = await organizerService.createTopic(
                directionId,
                name,
              );
              await loadOrganizer();
              return topic.id;
            }}
            onUpdateTopic={async (id, name) => {
              await organizerService.updateTopic(id, name);
              await loadOrganizer();
            }}
            onDeleteTopic={async (id) => {
              await organizerService.deleteTopic(id);
              await loadOrganizer();
            }}
            onCreateReply={async (topicId, text) => {
              await organizerService.createReply(topicId, text);
              await loadOrganizer();
            }}
            onUpdateReply={async (id, text) => {
              await organizerService.updateReply(id, text);
              await loadOrganizer();
            }}
            onDeleteReply={async (id) => {
              await organizerService.deleteReply(id);
              await loadOrganizer();
            }}
            onInsertReply={(text) => setInsertRequest({ id: Date.now(), text })}
            quickOpenRequest={quickRepliesRequest}
          />
        </div>
        <ChatWindow
          chat={selected}
          messages={messages}
          loading={loadingMessages}
          sending={sending}
          draft={selectedId ? draftsByChat[selectedId] || "" : ""}
          onDraftChange={changeDraft}
          onBack={closeChat}
          onSendText={sendText}
          onSendImage={sendImage}
          onDeleteMessage={deleteMessage}
          onLoadOlder={async () => {
            if (!selectedId) return 0;
            const older = await chatService.messages(
              selectedId,
              messages.length,
            );
            setMessages((items) => {
              const known = new Set(items.map((message) => message.id));
              return [
                ...older.filter((message) => !known.has(message.id)),
                ...items,
              ];
            });
            return older.length;
          }}
          insertRequest={insertRequest}
          onOpenQuickReplies={() =>
            setQuickRepliesRequest((request) => request + 1)
          }
          onOpenTeacherAssistant={teacherAssistantEnabled
            ? () => {
                if (selected)
                  setTeacherAssistant({
                    mode: "chat",
                    chatId: selected.id,
                    chatName: selected.name,
                  });
              }
            : undefined}
        />
      </div>
    </div>
  );
};

export default App;
